import { perkpointApi, type ApiPage } from "./client";

export type TxnType = "earn" | "redeem" | "adjust" | "expire" | "reverse";
export type TxnStatus = "completed" | "pending" | "failed" | "reversed";
export type TxnChannel = "in_app" | "pos" | "campaign" | "admin" | "system";

export type AppliedRule = {
  rule_id: string;
  rule_code: string;
  rule_name: string;
  rule_version?: number;
  kind: "earn" | "multiplier" | "redemption" | "validity";
  points?: number;
  multiplier?: number;
  validity_type?: string;
  days?: number;
};

export type ApiTransaction = {
  id: string;
  txn_code: string;
  created_at: string;
  type: TxnType;
  status: TxnStatus;
  points: number;
  requested_points: number | null;
  balance_after: number | null;
  description: string | null;
  channel: TxnChannel;
  outlet: string | null;
  reference_id: string | null;
  source_type: string | null;
  reason: string | null;
  failure_reason: string | null;
  available_at: string | null;
  expires_at: string | null;
  member: { member_id: string; external_member_id: string; name: string | null; email: string | null };
  event: { event_type: string; amount_minor: number | null; currency: string | null; items: unknown[] | null } | null;
  applied_rules: AppliedRule[];
  rule: { rule_id: string; code: string; name: string } | null;
  cost_per_point: number | null;
  currency: string | null;
  points_value: number | null;
  program: string;
  blockchain_tx_id: string | null;
  block_number: number | null;
  blockchain_explorer_url: string | null;
};

export type ApiTransactionDetail = ApiTransaction & {
  earn_rule?: { rule_id: string; code: string; name: string; version: number } | null;
  earn_multiplier?: number | string | null;
  validity_rule?: AppliedRule | null;
  reward_name?: string | null;
  redemption_code?: string | null;
  redemption_rule?: { rule_id: string; code: string; name: string } | null;
  adjusted_by?: string | null;
  adjust_reason?: string | null;
  expiry_rule?: string | null;
  original_earn_date?: string | null;
  original_txn_code?: string | null;
  reverses_txn_code?: string | null;
  reversals?: { txn_code: string; created_at: string; points: number }[];
  can_reverse?: boolean;
};

export type TxnTotals = {
  points_in: number;
  points_out: number;
  pending: number;
  failed: number;
  reversed: number;
};

export type TxnListQuery = {
  search?: string;
  type?: string;
  status?: string;
  channel?: string;
  member_id?: string;
  rule_code?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
};

export const TXN_CHANNEL_LABELS: Record<TxnChannel, string> = {
  in_app: "In-app",
  pos: "POS",
  campaign: "Campaign",
  admin: "Admin",
  system: "System",
};

export const txnKeys = {
  all: ["perkpoint", "transactions"] as const,
  list: (programId: string, query: TxnListQuery) =>
    ["perkpoint", "transactions", programId, "list", query] as const,
  detail: (programId: string, txnId: string) =>
    ["perkpoint", "transactions", programId, "detail", txnId] as const,
};

export const TransactionsApi = {
  async list(programId: string, query: TxnListQuery = {}) {
    const { data } = await perkpointApi.get<ApiPage<ApiTransaction> & { totals: TxnTotals }>(
      `/admin/programs/${programId}/transactions`,
      { params: query },
    );
    return data;
  },
  async get(programId: string, txnId: string) {
    const { data } = await perkpointApi.get<ApiTransactionDetail>(
      `/admin/programs/${programId}/transactions/${txnId}`,
    );
    return data;
  },
  async reverse(programId: string, txnId: string, reason: string) {
    const { data } = await perkpointApi.post<ApiTransactionDetail>(
      `/admin/programs/${programId}/transactions/${txnId}/reverse`,
      { reason },
    );
    return data;
  },
  /** Staff earn or redeem (Insert transaction screen, test data). */
  async create(
    programId: string,
    body:
      | { type: "earn"; member_id: string; reference_id: string; amount: string; outlet?: string; event_type?: string }
      | { type: "redeem"; member_id: string; reference_id: string; points: number; description?: string; outlet?: string },
  ) {
    const { data } = await perkpointApi.post<{
      points_awarded?: number;
      txn_code?: string;
      balance?: { available: number; pending: number };
    }>(`/admin/programs/${programId}/transactions`, body);
    return data;
  },
  /** One member's ledger (alias of the list filtered by member). */
  async forMember(programId: string, memberId: string, query: TxnListQuery = {}) {
    const { data } = await perkpointApi.get<ApiPage<ApiTransaction> & { totals: TxnTotals }>(
      `/admin/programs/${programId}/members/${memberId}/transactions`,
      { params: query },
    );
    return data;
  },
};
